import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Truck, Receipt, Lock, ArrowRight, Wallet as WalletIcon, Check } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useCart } from "../contexts/CartContext";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

const CheckoutContent = () => {
    const { cart, fetchCart } = useCart();
    const navigate = useNavigate();

    const [addresses, setAddresses] = useState([]);
    const [selectedAddrId, setSelectedAddrId] = useState(null);
    const [loadingAddrs, setLoadingAddrs] = useState(true);
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [quoteErr, setQuoteErr] = useState(null);
    const [paying, setPaying] = useState(false);
    const [useWallet, setUseWallet] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoError, setPromoError] = useState(null);
    const [promoBusy, setPromoBusy] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setLoadingAddrs(true);
                const { data } = await api.get("/addresses");
                setAddresses(data);
                const def = data.find((a) => a.is_default) || data[0];
                if (def) setSelectedAddrId(def.id);
            } finally {
                setLoadingAddrs(false);
            }
        };
        load();
    }, []);

    const fetchQuote = async (addressId, withWallet, code) => {
        if (!addressId) return;
        setQuoting(true);
        setQuoteErr(null);
        try {
            const body = { address_id: addressId, use_wallet: !!withWallet };
            if (code) body.promo_code = code;
            const { data } = await api.post("/checkout/quote", body);
            setQuote(data);
            if (code) setAppliedPromo(data.promo || null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Could not get quote";
            setQuoteErr(msg);
            setQuote(null);
        } finally {
            setQuoting(false);
        }
    };

    useEffect(() => { if (selectedAddrId) fetchQuote(selectedAddrId, useWallet, appliedPromo?.code || null); }, [selectedAddrId, useWallet, appliedPromo?.code]); // eslint-disable-line

    const applyPromo = async () => {
        const code = (promoCode || "").trim().toUpperCase();
        if (!code) return;
        setPromoBusy(true);
        setPromoError(null);
        try {
            const { data } = await api.post("/promos/validate", { code, cart_subtotal: quote?.subtotal || 0 });
            setAppliedPromo(data);
            toast.success(`${data.code} applied — ${data.free_shipping ? "free shipping" : `₹${data.discount} off`}`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Couldn't apply this code.";
            setPromoError(msg);
            setAppliedPromo(null);
        } finally {
            setPromoBusy(false);
        }
    };

    const removePromo = () => {
        setAppliedPromo(null);
        setPromoCode("");
        setPromoError(null);
    };

    const walletOnly = quote && quote.wallet_applied > 0 && Math.abs(quote.total) < 0.01;

    const onPay = async () => {
        if (!selectedAddrId || !quote || paying) return;
        setPaying(true);
        try {
            const body = { address_id: selectedAddrId, use_wallet: useWallet };
            if (appliedPromo?.code) body.promo_code = appliedPromo.code;
            const { data } = await api.post("/checkout/create-order", body);
            if (data.session_id === "wallet" || data.amount === 0) {
                toast.success(`Order ${data.order_no} paid via wallet`);
                await fetchCart?.();
                setTimeout(() => { navigate(`/orders/${data.order_id}`); }, 600);
            } else {
                toast.success(`Order ${data.order_no} created — redirecting to Zoho Payments...`);
                setTimeout(() => { window.location.href = data.redirect_url; }, 700);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            const code = typeof detail === "object" ? detail?.code : null;
            let msg;
            if (status === 502 || code === "PAYMENT_GATEWAY_ERROR") {
                msg = "Payment provider is currently unavailable. Please try again in a moment.";
            } else if (code === "INSUFFICIENT_WALLET") {
                msg = "Wallet balance changed. Refreshing...";
                await fetchQuote(selectedAddrId, useWallet);
            } else if (typeof detail === "string") {
                msg = detail;
            } else if (detail?.message) {
                msg = detail.message;
            } else {
                msg = "Could not start payment. Please try again.";
            }
            toast.error(msg, { duration: 6000 });
            setPaying(false);
        }
    };

    if (cart.items.length === 0) {
        return (
            <EmptyState
                title="Your cart is empty"
                description="Add some items before you can check out."
                action={<Link to="/products" className="btn-brand">Browse products</Link>}
            />
        );
    }

    if (loadingAddrs) {
        return (
            <div className="space-y-3">
                <div className="h-24 skeleton rounded-2xl" />
                <div className="h-48 skeleton rounded-2xl" />
            </div>
        );
    }

    if (addresses.length === 0) {
        return (
            <EmptyState
                title="No delivery address"
                description="Please add a delivery address before you can check out."
                action={<Link to="/account" className="btn-brand">Add address</Link>}
            />
        );
    }

    const walletBalance = quote?.wallet_balance ?? 0;
    const canUseWallet = walletBalance > 0;

    return (
        <div className="grid gap-8 lg:grid-cols-[1fr_380px]" data-testid="checkout-page">
            <div>
                {/* Address selector */}
                <section className="mb-6 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="checkout-address-section">
                    <p className="label-uppercase">Step 1</p>
                    <h2 className="mt-1 text-xl font-semibold tracking-tight">Delivery address</h2>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {addresses.map((a) => (
                            <label
                                key={a.id}
                                className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all ${selectedAddrId === a.id ? "border-brand-500 bg-brand-50/50 shadow-brand" : "border-zinc-200 hover:border-brand-300"}`}
                                data-testid={`addr-radio-${a.id}`}
                            >
                                <input
                                    type="radio"
                                    name="addr"
                                    checked={selectedAddrId === a.id}
                                    onChange={() => setSelectedAddrId(a.id)}
                                    className="mt-1 h-4 w-4 accent-brand-700"
                                />
                                <div>
                                    <p className="text-sm font-medium text-zinc-900">{a.label}{a.is_default && <span className="ml-2 text-[10px] text-brand-700">DEFAULT</span>}</p>
                                    <p className="mt-1 text-xs text-zinc-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                                    <p className="text-xs text-zinc-600">{a.city}, {a.state} - {a.pincode}</p>
                                    <p className="text-xs text-zinc-500">{a.phone}</p>
                                </div>
                            </label>
                        ))}
                    </div>
                    <Link to="/account" className="mt-4 inline-block text-xs font-medium text-brand-700 hover:underline">+ Manage addresses</Link>
                </section>

                {/* Items */}
                <section className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="checkout-items-section">
                    <p className="label-uppercase">Step 2</p>
                    <h2 className="mt-1 text-xl font-semibold tracking-tight">Review items</h2>
                    <ul className="mt-4 divide-y divide-zinc-100">
                        {cart.items.map((it) => (
                            <li key={it.product_id} className="flex items-center gap-4 py-3" data-testid={`checkout-item-${it.product_id}`}>
                                <img src={it.image} alt={it.name} className="h-14 w-14 rounded-xl object-cover ring-1 ring-zinc-200" />
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-1 text-sm font-medium">{it.name}</p>
                                    <p className="text-xs text-zinc-500">{it.brand} • {it.pack_size}</p>
                                    {it.prescription && (
                                        <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-700">
                                            <ShieldCheck className="h-3 w-3" /> Rx attached • {it.prescription.doctor_name || "Prescription"}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right text-sm">
                                    <p>{inr(it.selling_price)} × {it.qty}</p>
                                    <p className="font-semibold">{inr(it.line_total)}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>

            {/* Summary */}
            <aside className="self-start rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="checkout-summary">
                <p className="label-uppercase">Payment summary</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight">Order total</h2>

                {quoting && (
                    <div className="mt-4 space-y-2">
                        <div className="h-4 skeleton" />
                        <div className="h-4 skeleton" />
                        <div className="h-4 skeleton" />
                    </div>
                )}

                {quoteErr && (
                    <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-3 text-sm text-red-700" data-testid="checkout-quote-error">
                        {quoteErr}
                    </div>
                )}

                {quote && !quoting && (
                    <>
                        {/* Wallet toggle */}
                        <div
                            className={`mt-5 rounded-2xl border p-4 transition-colors ${
                                useWallet
                                    ? "border-brand-300 bg-gradient-to-br from-brand-50 to-white shadow-brand"
                                    : "border-zinc-100 bg-zinc-50/40"
                            } ${!canUseWallet ? "opacity-60" : ""}`}
                            data-testid="checkout-wallet-toggle-section"
                        >
                            <label className="flex cursor-pointer items-start gap-3">
                                <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${useWallet ? "border-brand-700 bg-brand-700 text-white" : "border-zinc-300 bg-white"}`}>
                                    {useWallet && <Check className="h-3 w-3" />}
                                </span>
                                <input
                                    type="checkbox"
                                    checked={useWallet}
                                    onChange={(e) => setUseWallet(e.target.checked)}
                                    disabled={!canUseWallet}
                                    className="sr-only"
                                    data-testid="checkout-wallet-toggle"
                                />
                                <div className="flex-1">
                                    <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                                        <WalletIcon className="h-3.5 w-3.5 text-brand-700" /> Use PILLS Wallet
                                    </p>
                                    <p className="mt-0.5 text-xs text-zinc-500">
                                        Available balance: <strong data-testid="checkout-wallet-balance">{inr(walletBalance)}</strong>
                                        {!canUseWallet && " — top up to use"}
                                    </p>
                                </div>
                            </label>
                        </div>

                        {/* Promo code */}
                        <div className="mt-4 rounded-2xl border border-zinc-100 bg-white p-4" data-testid="checkout-promo-section">
                            {appliedPromo ? (
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                                            <Check className="h-3.5 w-3.5" /> {appliedPromo.code} applied
                                        </p>
                                        <p className="mt-0.5 truncate text-xs text-zinc-500">
                                            {appliedPromo.free_shipping ? "Free shipping" : `₹${appliedPromo.discount} off subtotal`}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removePromo}
                                        className="rounded-md border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50"
                                        data-testid="checkout-promo-remove"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Have a promo code?</p>
                                    <div className="mt-2 flex gap-2">
                                        <input
                                            type="text"
                                            value={promoCode}
                                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null); }}
                                            placeholder="WELCOME100"
                                            className="flex-1 rounded-md border border-zinc-200 px-3 py-2 text-sm uppercase tracking-wider focus:border-brand-400 focus:outline-none"
                                            data-testid="checkout-promo-input"
                                            autoComplete="off"
                                        />
                                        <button
                                            type="button"
                                            onClick={applyPromo}
                                            disabled={promoBusy || !promoCode.trim()}
                                            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                                            data-testid="checkout-promo-apply"
                                        >
                                            {promoBusy ? "…" : "Apply"}
                                        </button>
                                    </div>
                                    {promoError && (
                                        <p className="mt-2 text-xs text-red-600" data-testid="checkout-promo-error">{promoError}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <dl className="mt-5 space-y-3 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-zinc-500">Subtotal</dt>
                                <dd className="font-medium" data-testid="quote-subtotal">{inr(quote.subtotal)}</dd>
                            </div>
                            {quote.discount > 0 && (
                                <div className="flex justify-between text-emerald-700" data-testid="quote-discount-row">
                                    <dt className="flex items-center gap-1.5">Promo {quote.promo ? `(${quote.promo.code})` : ""}</dt>
                                    <dd className="font-medium" data-testid="quote-discount">−{inr(quote.discount)}</dd>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <dt className="text-zinc-500 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Shipping
                                    {quote.shipping_free_elite && <span className="rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-amber-700">Elite</span>}
                                </dt>
                                <dd className="font-medium" data-testid="quote-shipping">
                                    {quote.shipping_fee === 0 ? <span className="text-emerald-700">Free</span> : inr(quote.shipping_fee)}
                                </dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-zinc-500 flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> GST {quote.gst_rate_percent}%</dt>
                                <dd className="font-medium" data-testid="quote-tax">{inr(quote.tax_gst)}</dd>
                            </div>
                            {quote.wallet_applied > 0 && (
                                <div className="flex justify-between text-brand-700" data-testid="quote-wallet-row">
                                    <dt className="flex items-center gap-1.5"><WalletIcon className="h-3.5 w-3.5" /> Wallet applied</dt>
                                    <dd className="font-medium" data-testid="quote-wallet-applied">−{inr(quote.wallet_applied)}</dd>
                                </div>
                            )}
                            <div className="flex justify-between border-t border-zinc-100 pt-3">
                                <dt className="font-medium text-zinc-900">Total</dt>
                                <dd className="text-lg font-semibold text-zinc-900" data-testid="quote-total">{inr(quote.total)}</dd>
                            </div>
                        </dl>

                        <div className="mt-4 rounded-2xl border border-brand-100 bg-brand-50/40 p-3 text-xs">
                            <p className="font-medium text-brand-800">{quote.shipping.courier_name}</p>
                            <p className="mt-0.5 text-brand-700/80">
                                {quote.shipping.serviceable ? `ETA ${quote.shipping.etd || "3–5 days"}` : "Default delivery (couriers unavailable to this pincode right now)"}
                                {quote.shipping.source === "fallback" && " • flat rate"}
                            </p>
                        </div>

                        <button
                            onClick={onPay}
                            disabled={paying}
                            className="btn-brand mt-6 w-full"
                            data-testid="checkout-pay-btn"
                        >
                            {paying ? (
                                walletOnly ? "Confirming..." : "Connecting to Zoho..."
                            ) : walletOnly ? (
                                <>
                                    <WalletIcon className="h-4 w-4" /> Pay with wallet <ArrowRight className="h-4 w-4" />
                                </>
                            ) : (
                                <>
                                    <Lock className="h-4 w-4" /> Pay {inr(quote.total)} <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>

                        <p className="mt-3 text-center text-[11px] text-zinc-500">
                            {walletOnly ? (
                                <>Paid instantly from your <span className="font-medium text-brand-700">PILLS Wallet</span></>
                            ) : (
                                <>Secure payments processed by <span className="font-medium text-brand-700">Zoho Payments</span></>
                            )}
                        </p>
                    </>
                )}
            </aside>
        </div>
    );
};

const Checkout = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Secure checkout</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Checkout</h1>
            </header>
            <AuthGuard>
                <CheckoutContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Checkout;
